import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import icons from "@treefort/tokens/app/icons"
import { minTapTarget } from "@treefort/tokens/app/tokens/base"

import { useTracks, useSkipMode, useLoopMode } from "../hooks/audio"
import { useBooleanState } from "../hooks/use-boolean-state"
import {
  audioPlayer,
  SKIP_BACKWARD_INTERVAL,
  SKIP_FORWARD_INTERVAL,
} from "../lib/audio-player"
import AudioPlayerPlayButton from "./audio-player-play-button"
import Column from "./column"
import IconButton from "./icon-button"
import { PlaybackRateSelect } from "./playback-rate-select"
import { PlaybackTimerSelect } from "./playback-timer-select"
import Row from "./row"
import Select from "./select"

const SECONDARY_CONTROLS_CONTAINER_WEB_HEIGHT = 36

export default function AudioPlayerControls({
  compact,
  onPressNavigateToContent,
}: {
  compact?: boolean
  onPressNavigateToContent: () => void
}): React.JSX.Element {
  const [trackPickerOpen, openTrackPicker, closeTrackPicker] =
    useBooleanState(false)
  const tracks = useTracks()
  const skipMode = useSkipMode()
  const loopMode = useLoopMode()
  const skipPositionBackward = useCallback(
    () => audioPlayer.skipPosition(-SKIP_BACKWARD_INTERVAL),
    [],
  )
  const skipPositionForward = useCallback(
    () => audioPlayer.skipPosition(SKIP_FORWARD_INTERVAL),
    [],
  )
  const buttonSize = compact
    ? SECONDARY_CONTROLS_CONTAINER_WEB_HEIGHT
    : minTapTarget

  const onPickTrack = useCallback(
    async (id: string) => {
      closeTrackPicker()
      await audioPlayer.skipToTrack(id, { autoPlay: true })
    },
    [closeTrackPicker],
  )

  const isAlbum = tracks.every((track) => track.extra.contentType === "album")

  const includeTrackPicker = tracks.length > 1
  const includeRateSelect = !isAlbum
  const includeLoopModeToggle = isAlbum

  const { t } = useTranslation()

  const iconButtonMinSize = compact ? "disabled" : undefined

  return (
    <Column
      gap={compact ? "none" : "xlarge"}
      justifyContent="space-between"
      alignItems="center"
      width="100%"
    >
      <Row
        width="100%"
        gap={compact ? "xtiny" : "jumbo"}
        justifyContent="space-around"
        paddingHorizontal="tiny"
      >
        {skipMode === "track" ? (
          <IconButton
            id="audio-player-controls-skip-backward"
            onPress={audioPlayer.skipBackward}
            iconSize={compact ? "large" : "xlarge"}
            source={icons.skipBackward}
            label={t("Previous")}
            minSize={iconButtonMinSize}
            height={buttonSize}
            width={buttonSize}
          />
        ) : (
          <IconButton
            id="audio-player-controls-skip-backward"
            onPress={skipPositionBackward}
            iconSize={compact ? "large" : "xlarge"}
            source={
              SKIP_BACKWARD_INTERVAL === 10000 ? icons.back10 : icons.back30
            }
            label={t("Skip backward {{number}} seconds", {
              number: SKIP_BACKWARD_INTERVAL / 1000,
            })}
            minSize={iconButtonMinSize}
            height={buttonSize}
            width={buttonSize}
          />
        )}
        <AudioPlayerPlayButton iconSize={compact ? "large" : "jumbo"} />
        {skipMode === "track" ? (
          <IconButton
            id="audio-player-controls-skip-forward"
            onPress={audioPlayer.skipForward}
            iconSize={compact ? "large" : "xlarge"}
            source={icons.skipForward}
            label={t("Next")}
            minSize={iconButtonMinSize}
            height={buttonSize}
            width={buttonSize}
          />
        ) : (
          <IconButton
            id="audio-player-controls-skip-forward"
            onPress={skipPositionForward}
            iconSize={compact ? "large" : "xlarge"}
            source={
              SKIP_FORWARD_INTERVAL === 10000
                ? icons.forward10
                : icons.forward30
            }
            label={t("Skip forward {{number}} seconds", {
              number: SKIP_FORWARD_INTERVAL / 1000,
            })}
            minSize={iconButtonMinSize}
            height={buttonSize}
            width={buttonSize}
          />
        )}
      </Row>
      <Row
        gap="large"
        width="100%"
        justifyContent="space-between"
        paddingHorizontal="small"
        borderRadius="rounded"
        backgroundColor="secondary"
      >
        {includeLoopModeToggle ? (
          <IconButton
            id="audio-player-controls-loop"
            onPress={audioPlayer.toggleLoopMode}
            iconSize="medium"
            source={loopMode === "one" ? icons.loopOne : icons.loopAll}
            color={loopMode === "off" ? undefined : "accent"}
            label={t("Loop")}
            minSize={iconButtonMinSize}
            height={buttonSize}
            width={buttonSize}
          />
        ) : null}
        {includeRateSelect ? <PlaybackRateSelect /> : null}
        <PlaybackTimerSelect
          iconSize="medium"
          minSize={compact ? "disabled" : undefined}
          height={buttonSize}
          width={buttonSize}
        />
        <IconButton
          id="audio-player-controls-open-track-picker"
          onPress={openTrackPicker}
          source={icons.menuStaggered}
          iconSize="medium"
          label={t("Open track picker")}
          disabled={!includeTrackPicker}
          minSize={iconButtonMinSize}
          height={buttonSize}
          width={buttonSize}
        />
        <IconButton
          id="audio-player-controls-navigate-to-content-page"
          onPress={onPressNavigateToContent}
          source={icons.infoCircle}
          iconSize="medium"
          label={t("View")}
          minSize={iconButtonMinSize}
          height={buttonSize}
          width={buttonSize}
        />
        {includeTrackPicker ? (
          <Select
            id="audio-player-controls-close-track-picker"
            label={tracks[0]?.album}
            open={trackPickerOpen}
            onClose={closeTrackPicker}
            value={audioPlayer.getCurrentTrackId()}
            onChange={onPickTrack}
            numbered={isAlbum}
            options={tracks.map((track) => ({
              label: track.title,
              value: track.id,
            }))}
          />
        ) : null}
      </Row>
    </Column>
  )
}
